import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup,
  CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cfopService } from "@/features/fiscal/services/fiscalService";

interface Props {
  value?:       number | null;   // código CFOP (número) — ou Id da entidade quando byId
  onChange:     (valor: number | null) => void;
  /** Quando true, value/onChange usam o Id da entidade CFOP em vez do código. */
  byId?:        boolean;
  placeholder?: string;
  disabled?:    boolean;
}

export function CfopSearchCombobox({
  value,
  onChange,
  byId = false,
  placeholder = "Selecionar CFOP...",
  disabled,
}: Props) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["cfop-combo-search", search],
    queryFn:  () => cfopService.combo(search || undefined),
    enabled:  open,
    staleTime: 60_000,
  });
  const results = data ?? [];

  // Resolve o rótulo do CFOP atualmente selecionado
  const { data: selecionado } = useQuery({
    queryKey: ["cfop-combo-selected", value, byId],
    queryFn:  () => byId
      ? cfopService.obterPorId(value as number).then(d => [{ id: d.id, codigo: d.codigo, descricao: d.descricao }])
      : cfopService.combo(value != null ? String(value) : undefined),
    enabled:  value != null,
    staleTime: 60_000,
    select:   (list) => list.find(c => (byId ? c.id : Number(c.codigo)) === value),
  });

  const label = selecionado
    ? `${selecionado.codigo} — ${selecionado.descricao ?? ""}`
    : value != null ? String(value) : placeholder;

  return (
    <div className="flex items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled}
            type="button"
          >
            <span className={cn("truncate text-left", value == null && "text-muted-foreground")}>{label}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[440px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Digite código ou descrição do CFOP..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isLoading && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!isLoading && <CommandEmpty>Nenhum CFOP encontrado.</CommandEmpty>}
              <CommandGroup>
                {results.map(c => {
                  const val = byId ? c.id : Number(c.codigo);
                  return (
                    <CommandItem
                      key={c.id}
                      value={String(c.codigo)}
                      onSelect={() => { onChange(val); setOpen(false); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4 shrink-0", value === val ? "opacity-100" : "opacity-0")} />
                      <div className="flex flex-col min-w-0">
                        <span className="font-mono text-sm">{c.codigo}</span>
                        <span className="text-xs text-muted-foreground truncate">{c.descricao}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value != null && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          title="Limpar"
          onClick={() => onChange(null)}
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </Button>
      )}
    </div>
  );
}
